import React, { Component } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { FileText, Plus, Edit2, Trash2 } from 'lucide-react';
import { fetchProposals, updateProposal, deleteProposal } from '../actions/proposals';
import '../styles/ProposalConfiguratorView.css';
import '../styles/ProposalsView.css';

// ─── Design tokens (same palette as rest of proposal views) ──
const TEAL   = '#00b4d8';
const RED    = '#ef4444';
const TEXT_1 = '#1e293b';
const TEXT_2 = '#64748b';
const BORDER = '#e2e8f0';

const btnPrimary = {
  background: TEAL, border: 'none', color: '#fff',
  padding: '9px 18px', borderRadius: 8, fontSize: 13, fontWeight: 600,
  cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
};
const btnSmall = {
  ...btnPrimary, padding: '6px 12px', fontSize: 12,
};
const btnDanger = { ...btnSmall, background: RED };
const btnOutline = {
  ...btnSmall, background: 'transparent',
  border: `1px solid ${BORDER}`, color: TEXT_1,
};

const STATUS_LABELS = {
  draft:    'Borrador',
  sent:     'Enviada',
  accepted: 'Aceptada',
  rejected: 'Rechazada',
};

const MODE_LABELS = {
  purchase: 'Venta',
  rent:     'Alquiler',
  revenue:  'Explotación',
};

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function totalLockers(proposal) {
  const items = [...(proposal.cabinetItems || []), ...(proposal.posItems || [])];
  return items.reduce((sum, it) => sum + (it.lockers || 0) * (it.qty || 1), 0);
}

const STATUS_FILTERS = ['all', 'draft', 'sent', 'accepted', 'rejected'];

class ProposalsView extends Component {
  constructor(props) {
    super(props);
    this.state = {
      statusFilter: 'all',
      deletingId: null,
    };
  }

  componentDidMount() {
    this.props.fetchProposals();
  }

  componentDidUpdate(prevProps) {
    if (this.props.needsRefresh && !prevProps.needsRefresh) {
      this.props.fetchProposals();
    }
  }

  handleEdit(proposal) {
    this.props.history.push('/proposal-configurator', { loadedProposal: proposal });
  }

  handleStatusChange(proposal, status) {
    this.props.updateProposal(proposal._id, { status });
  }

  handleDeleteConfirm() {
    const { deletingId } = this.state;
    if (!deletingId) return;
    this.props.deleteProposal(deletingId);
    this.setState({ deletingId: null });
  }

  filteredProposals() {
    const { proposals } = this.props;
    const { statusFilter } = this.state;
    if (!proposals) return [];
    if (statusFilter === 'all') return proposals;
    return proposals.filter(p => p.status === statusFilter);
  }

  renderStatusBadge(status) {
    return (
      <span className={`status-badge status-${status}`}>
        {STATUS_LABELS[status] || status}
      </span>
    );
  }

  renderDeleteModal() {
    const { deletingId } = this.state;
    if (!deletingId) return null;
    return (
      <div className="confirm-overlay" onClick={() => this.setState({ deletingId: null })}>
        <div className="confirm-modal" onClick={e => e.stopPropagation()}>
          <h3>Eliminar propuesta</h3>
          <p>Esta acción es irreversible. ¿Seguro que quieres eliminarla?</p>
          <div className="confirm-modal-actions">
            <button style={btnOutline} onClick={() => this.setState({ deletingId: null })}>
              Cancelar
            </button>
            <button style={btnDanger} onClick={() => this.handleDeleteConfirm()}>
              Eliminar
            </button>
          </div>
        </div>
      </div>
    );
  }

  renderTable() {
    const proposals = this.filteredProposals();

    if (this.props.loading) {
      return <div className="proposals-loading">Cargando propuestas…</div>;
    }

    if (!proposals.length) {
      return (
        <div className="proposals-empty">
          <FileText size={48} />
          <p>No hay propuestas{this.state.statusFilter !== 'all' ? ` con estado "${STATUS_LABELS[this.state.statusFilter]}"` : ' aún'}.</p>
        </div>
      );
    }

    return (
      <table className="proposals-table">
        <thead>
          <tr>
            <th>REF / CLIENTE</th>
            <th>EMPRESA</th>
            <th>COMERCIAL</th>
            <th>FECHA</th>
            <th>MODO</th>
            <th>LOCKERS</th>
            <th>ESTADO</th>
            <th>ACCIONES</th>
          </tr>
        </thead>
        <tbody>
          {proposals.map(p => (
            <tr key={p._id}>
              <td>
                <div style={{ fontWeight: 600, color: TEXT_1 }}>{p.proposalRef || '—'}</div>
                <div style={{ fontSize: 12, color: TEXT_2 }}>{p.clientName || '—'}</div>
              </td>
              <td className="secondary">{p.clientCompany || '—'}</td>
              <td className="secondary">{p.createdByName || '—'}</td>
              <td className="secondary">{formatDate(p.createdAt)}</td>
              <td className="secondary">{MODE_LABELS[p.saleMode] || p.saleMode}</td>
              <td className="secondary">{totalLockers(p) || '—'}</td>
              <td>
                <select
                  className="status-select"
                  value={p.status}
                  onChange={e => this.handleStatusChange(p, e.target.value)}
                >
                  {Object.entries(STATUS_LABELS).map(([val, label]) => (
                    <option key={val} value={val}>{label}</option>
                  ))}
                </select>
              </td>
              <td>
                <div className="proposals-actions">
                  <button
                    style={btnSmall}
                    title="Editar en configurador"
                    onClick={() => this.handleEdit(p)}
                  >
                    <Edit2 size={13} /> Editar
                  </button>
                  <button
                    style={btnDanger}
                    title="Eliminar propuesta"
                    onClick={() => this.setState({ deletingId: p._id })}
                  >
                    <Trash2 size={13} />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  }

  render() {
    const { statusFilter } = this.state;

    return (
      <div className="proposals-view-root">
        {/* Hero ─────────────────────────────────────────── */}
        <div className="pconf-header">
          <div className="pconf-header-content">
            <div className="pconf-header-text">
              <h1>
                <FileText size={28} className="pconf-header-icon" />
                Propuestas comerciales
              </h1>
              <span className="pconf-subtitle">
                Gestiona y edita las propuestas guardadas
              </span>
            </div>
            <div className="pconf-header-actions">
              <button
                className="pconf-header-btn primary"
                onClick={() => this.props.history.push('/proposal-configurator')}
              >
                <Plus size={16} /> Nueva propuesta
              </button>
            </div>
          </div>
        </div>

        {/* Body ─────────────────────────────────────────── */}
        <div className="proposals-body">

          {/* Filters */}
          <div className="proposals-filters">
            {STATUS_FILTERS.map(f => (
              <button
                key={f}
                className={`proposals-filter-btn${statusFilter === f ? ' active' : ''}`}
                onClick={() => this.setState({ statusFilter: f })}
              >
                {f === 'all' ? 'Todas' : STATUS_LABELS[f]}
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="proposals-table-wrap">
            {this.renderTable()}
          </div>
        </div>

        {/* Delete confirm modal */}
        {this.renderDeleteModal()}
      </div>
    );
  }
}

const mapStateToProps = state => ({
  proposals:    state.proposals.proposals,
  loading:      state.proposals.loading,
  needsRefresh: state.proposals.needsRefresh,
  error:        state.proposals.error,
});

const mapDispatchToProps = dispatch =>
  bindActionCreators({ fetchProposals, updateProposal, deleteProposal }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(ProposalsView);
